import type { Celebration } from 'src/sections/live-tracker/live-celebration';

import { useRef, useState, useEffect } from 'react';

import { useNotificationSoundSettings } from 'src/hooks/use-notification-sounds';

import { fCurrencyWithCode } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';

import { useLiveActivity, useLiveTrackerSounds } from './use-live-activity';

// ----------------------------------------------------------------------

const REFRESH_MS = 5000;
const CELEBRATION_MS = 4500;

/**
 * Detecta pedidos pagados y recargas de saldo NUEVOS (comparando con la última foto vista) y
 * expone lo necesario para celebrarlos: viven aquí, en un único sitio, para que el aviso
 * (confeti + cartel + sonido) se pueda montar una sola vez a nivel global (cualquier pantalla
 * del backoffice) en vez de duplicarse en cada componente que lo necesite.
 *
 * Solo Administrador: ni sondea ni celebra nada para el resto de roles.
 */
export function useLiveCelebration() {
  const { isAdmin } = useAuthContext();
  const active = isAdmin();

  const { enabled: soundEnabled, setEnabled: setSoundEnabled } = useNotificationSoundSettings();

  const { events, serverTimeUtc } = useLiveActivity({
    refreshInterval: active ? REFRESH_MS : 0,
    enabled: active,
  });
  const { soundByKind } = useLiveTrackerSounds(active);

  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const lastCelebratedIdRef = useRef(0);
  const feedInitializedRef = useRef(false);

  useEffect(() => {
    if (!active || !serverTimeUtc) return;

    // Se celebra tanto la compra de un pedido como una recarga de saldo (dinero que entra).
    const lastWin = events.find(
      (event) => event.type === 'order_placed' || event.type === 'recharge_completed'
    );

    // Primera foto: se toma como punto de partida (lo que ya estaba al entrar no se celebra),
    // pero a partir de aquí sí se celebra lo primero que entre, estés donde estés.
    if (!feedInitializedRef.current) {
      feedInitializedRef.current = true;
      lastCelebratedIdRef.current = lastWin?.id ?? 0;
      return;
    }

    if (lastWin && lastWin.id > lastCelebratedIdRef.current) {
      lastCelebratedIdRef.current = lastWin.id;
      const isRecharge = lastWin.type === 'recharge_completed';

      setCelebration({
        id: lastWin.id,
        kind: isRecharge ? 'recharge' : 'order',
        // El estilo/título sigue diciendo pedido o recarga; solo el sonido cambia si es "grande".
        soundKind: lastWin.isBigAmount ? 'big' : isRecharge ? 'recharge' : 'order',
        title: isRecharge ? '¡Recarga de saldo!' : '¡Nuevo pedido!',
        subtitle: [
          lastWin.userName || lastWin.userEmail || `Usuario #${lastWin.userId}`,
          typeof lastWin.amount === 'number' && lastWin.amount > 0
            ? fCurrencyWithCode(lastWin.amount, lastWin.currency || 'EUR')
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
      });
    }
  }, [events, serverTimeUtc, active]);

  useEffect(() => {
    if (!celebration) return undefined;
    const timer = window.setTimeout(() => setCelebration(null), CELEBRATION_MS);
    return () => window.clearTimeout(timer);
  }, [celebration]);

  return {
    celebration: active ? celebration : null,
    soundEnabled,
    setSoundEnabled,
    soundByKind,
  };
}
